import { state, snapshot, applyData } from "./state";
import { projTitle, ioModal, projectModal, populateBtn, copyToClipboard } from "./dom";
import {
  commit, render, focusName, focusNewName, toggleActiveTag, closeCommentPop, commentIsOpen, jiraTicketUrl,
} from "./view";
import {
  save, setStatus, flashStatus, hideConflict, switchToServer, switchToClient, relinkServerVersion, captureBase,
} from "./persistence";
import { addRoot, resyncEstimateTotals } from "../shared/mutations";
import { applyEstimationEdits } from "../shared/estimations";
import { anyExpanded, setAllCollapsed } from "../shared/tree";
import { randomTree } from "../shared/random";
import { normalize } from "../shared/model";
import { treeToOutline, parseOutline } from "../shared/outline";
import { leafLines } from "../shared/paths";
import { buildReport } from "../shared/report";
import { buildHtmlExport, htmlExportFilename, type HtmlExportCtx } from "../shared/htmlExport";
import { exportDoc, exportFilename, slug, fmt, type ExportCtx } from "../shared/format";
import { toJiraCsv, parseJiraCsv, planJiraImport, applyJiraPlan, compareJira, compareReport, type JiraPlan, type JiraField, type JiraCompare, type CompareChange, type CompareScope } from "../shared/jira";
import { parseVersionLines, applyVersionEdits } from "../shared/versions";
import { diffPlans } from "../shared/diff";
import { listRecents, removeRecent } from "./recents";
import { clearCollapse } from "./collapse";
import { clearPrefs } from "./prefs";

const byId = (id: string) => document.getElementById(id) as HTMLElement;
const asInput = (id: string) => document.getElementById(id) as HTMLInputElement;
const asArea = (id: string) => document.getElementById(id) as HTMLTextAreaElement;

function exportCtx(): ExportCtx {
  return {
    projectName: state.projectName, mode: state.mode, serverVersion: state.serverVersion,
    changes: state.changes, lastModified: state.lastModified,
    showTags: state.showTags, showVersions: state.showVersions, showComponents: state.showComponents,
    showDone: state.showDone, showJira: state.showJira, showGantt: state.showGantt,
    jiraUrl: state.jiraUrl, jiraDelim: state.jiraDelim,
    countDeps: state.countDeps, depDocOrder: state.depDocOrder, showComplexity: state.showComplexity, estimations: state.estimations, estimateView: state.estimateView,
    defaultEstimate: state.defaultEstimate,
    versions: state.versions, components: state.components, tree: state.tree,
  };
}

/* ---------- toolbar buttons ---------- */
byId("addRoot").onclick = () => { const id = addRoot(state.tree, state.defaultEstimate); commit(); focusNewName(id); };

populateBtn.onclick = () => { state.tree = randomTree(); commit(); };

byId("printBtn").onclick = () => window.print();

/* view mode — the segmented WBS ⇄ Version toggle in the header. It's a per-user view pref
   (localStorage via persistPrefs, no server save) → render(), which re-syncs the segments. */
function setView(view: "tree" | "version"): void {
  if (state.view === view) return; // clicking the already-active segment is a no-op
  state.view = view;
  render();
}
byId("viewTreeBtn").onclick = () => setView("tree");
byId("viewVersionBtn").onclick = () => setView("version");

/* ---------- local ↔ server ---------- */
export function updateModeBtn(): void {
  byId("modeBtn").textContent = state.mode === "server" ? "Switch to local" : "Save on server";
  const serverOnly = state.mode !== "server"; // cloning/history need a server-assigned guid
  byId("cloneBtn").hidden = serverOnly;
  byId("historyBtn").hidden = serverOnly;
}
byId("modeBtn").onclick = () => (state.mode === "server" ? switchToClient() : switchToServer());

/* ---------- Clone (server mode only): duplicate the plan into a new guid, open in a new tab ---------- */
byId("cloneBtn").onclick = async () => {
  if (state.mode !== "server") return;
  const doc: any = snapshot(); // full plan (tree, versions, components, view flags)
  doc.name = "Clone of " + (state.projectName.trim() || "Untitled project");
  try {
    const res = await fetch("/api/projects", {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ data: doc }),
    });
    if (!res.ok) throw new Error("HTTP " + res.status);
    const body = await res.json();
    window.open("/plan/" + body.guid, "_blank"); // open the clone in a new tab
  } catch (e: any) { alert("Could not clone this plan: " + e.message); }
};

/* ---------- Version history + "use this version" (server mode only) ---------- */
const historyModal = byId("historyModal");
const historyBody = byId("historyBody");
function closeHistoryModal() { historyModal.hidden = true; }
byId("historyClose").onclick = closeHistoryModal;
historyModal.addEventListener("click", (e) => { if (e.target === historyModal) closeHistoryModal(); });

interface HistoryEntry { version: number; savedAt: string | null; name: string | null; data?: any }
function fmtWhen(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? iso : d.toLocaleString();
}

byId("historyBtn").onclick = async () => {
  if (state.mode !== "server" || !state.guid) return;
  historyBody.innerHTML = '<p class="jr-empty">Loading…</p>';
  historyModal.hidden = false;
  try {
    const res = await fetch("/api/projects/" + state.guid + "/history?full=1"); // full data → on-page diffs
    if (!res.ok) throw new Error("HTTP " + res.status);
    renderHistory(((await res.json()).versions || []) as HistoryEntry[]);
  } catch { historyBody.innerHTML = '<p class="jr-summary">Could not load history.</p>'; }
};

// versions come newest-first; each version's diff is (previous version → this one) —
// i.e. "these changes produced this version". The oldest is the initial version.
function renderHistory(versions: HistoryEntry[]): void {
  historyBody.innerHTML = "";
  if (!versions.length) { historyBody.innerHTML = '<p class="jr-empty">No versions yet.</p>'; return; }
  versions.forEach((v, i) => {
    const row = document.createElement("div"); row.className = "hist-row";
    const vs = document.createElement("span"); vs.className = "hist-v"; vs.textContent = "v" + v.version;
    const when = document.createElement("span"); when.className = "hist-when";
    when.textContent = fmtWhen(v.savedAt) + (v.name ? " · " + v.name : "");
    row.appendChild(vs); row.appendChild(when);
    if (v.version === state.serverVersion) {
      const cur = document.createElement("span"); cur.className = "hist-cur"; cur.textContent = "current";
      row.appendChild(cur);
    } else {
      // "Use this version" (not "Roll back") — the action picks this version as the new
      // starting point; it doesn't undo a range of history.
      const btn = document.createElement("button"); btn.className = "tool"; btn.textContent = "Use this version";
      btn.title = `Continue from v${v.version}: its contents are saved as a new latest version. Nothing is deleted.`;
      btn.onclick = () => rollbackTo(v.version);
      row.appendChild(btn);
    }
    historyBody.appendChild(row);

    // inline diff: what changed from the previous version to produce this one
    const prev = versions[i + 1]; // next in the newest-first list = older version
    const diff = document.createElement("div"); diff.className = "hist-diff";
    if (!prev) {
      diff.innerHTML = '<span class="hist-init">Initial version</span>';
    } else {
      const lines = diffPlans(prev.data, v.data);
      if (!lines.length) diff.innerHTML = '<span class="hist-init">No content changes</span>';
      else {
        const ul = document.createElement("ul");
        const shown = lines.slice(0, 20);
        shown.forEach((l) => { const li = document.createElement("li"); li.textContent = l; ul.appendChild(li); });
        if (lines.length > shown.length) { const li = document.createElement("li"); li.className = "hist-more"; li.textContent = `+${lines.length - shown.length} more…`; ul.appendChild(li); }
        diff.appendChild(ul);
      }
    }
    historyBody.appendChild(diff);
  });
}

// "use this version" = re-save the chosen version's contents as a new latest version
// (nothing deleted, and the versions in between stay in the list)
async function rollbackTo(version: number): Promise<void> {
  try {
    const res = await fetch("/api/projects/" + state.guid + "/history/" + version);
    if (!res.ok) throw new Error("HTTP " + res.status);
    const snap = await res.json();
    applyData(snap.data || {});
    projTitle.value = state.projectName;
    document.title = state.projectName.trim() || "Untitled project";
    syncViewDialog();
    closeHistoryModal();
    commit(); // saves as latest+1 and re-renders
  } catch { historyBody.innerHTML = '<p class="jr-summary">Could not switch to that version.</p>'; }
}

byId("conflictReload").onclick = () => {
  const bar = byId("conflictBar");
  state.serverVersion = Number(bar.dataset.serverVersion) || state.serverVersion;
  applyData(JSON.parse(bar.dataset.serverData || "{}"));
  hideConflict();
  captureBase(); // adopted the server's copy → it's the new merge base
  projTitle.value = state.projectName;
  syncViewDialog();
  render();
  setStatus("idle");
  flashStatus();
};
byId("conflictOverwrite").onclick = () => {
  const bar = byId("conflictBar");
  state.serverVersion = Number(bar.dataset.serverVersion) || state.serverVersion;
  captureBase(); // force-push baseline; next 409 merges against this
  hideConflict();
  save();
};

byId("collapseBtn").onclick = () => { setAllCollapsed(state.tree, anyExpanded(state.tree)); render(); }; // view-only (localStorage, no save)

/* ---------- View dialog (show/hide columns) ---------- */
const viewTags = asInput("viewTags");
const viewVersions = asInput("viewVersions");
const viewComponents = asInput("viewComponents");
const viewDone = asInput("viewDone");
const viewJira = asInput("viewJira");
const viewGantt = asInput("viewGantt");

// mirror the current state into the dialog's checkboxes (call after any external state change)
export function syncViewDialog(): void {
  viewTags.checked = state.showTags;
  viewVersions.checked = state.showVersions;
  viewComponents.checked = state.showComponents;
  viewDone.checked = state.showDone;
  viewJira.checked = state.showJira;
  viewGantt.checked = state.showGantt;
}
// show-column toggles are per-user view prefs (localStorage, no server save) → render(), not commit()
viewTags.onchange = () => { state.showTags = viewTags.checked; render(); };
viewVersions.onchange = () => { state.showVersions = viewVersions.checked; render(); };
viewComponents.onchange = () => { state.showComponents = viewComponents.checked; render(); };
viewDone.onchange = () => { state.showDone = viewDone.checked; render(); };
viewJira.onchange = () => { state.showJira = viewJira.checked; render(); };
viewGantt.onchange = () => { state.showGantt = viewGantt.checked; render(); };

// the show-column checkboxes now live in the View ▾ menu; syncViewDialog() keeps them in step
// with state (called on load / external change). No modal to open anymore.

/* ---------- New ---------- */
byId("newBtn").onclick = async () => {
  if (!confirm("Start a new empty plan?")) return;
  if (state.mode === "server") {
    try {
      const res = await fetch("/api/projects", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ data: { name: "Untitled project", showTags: state.showTags, showVersions: state.showVersions, showDone: state.showDone, versions: [], tree: [] } }),
      });
      if (!res.ok) throw new Error("HTTP " + res.status);
      const body = await res.json();
      location.href = "/plan/" + body.guid;
    } catch (e: any) { alert("Could not create a new plan: " + e.message); }
  } else {
    state.tree = []; state.projectName = "Untitled project"; state.versions = [];
    projTitle.value = state.projectName;
    document.title = "Untitled project";
    syncViewDialog();
    commit();
  }
};

/* ---------- editable project title ---------- */
projTitle.oninput = () => {
  state.projectName = projTitle.value;
  document.title = state.projectName.trim() || "Untitled project";
};
projTitle.onblur = () => save();
projTitle.onkeydown = (e) => { if (e.key === "Enter") projTitle.blur(); };

/* ---------- Import/Export modal (Report · Text · JSON · Tasks) ---------- */
const textInput = asArea("textInput");
const textAppend = asInput("textAppend");
const jsonInput = asArea("jsonInput");
const jsonAppend = asInput("jsonAppend");
const tasksOutput = asArea("tasksOutput");
const reportOutput = asArea("reportOutput");
const jiraOutput = asArea("jiraOutput");
const jiraNewOnly = asInput("jiraNewOnly");
const jiraVersionedOnly = asInput("jiraVersionedOnly");
const compareInput = asArea("compareInput");
const compareResult = byId("compareResult");

function fillTextPane() { textInput.value = treeToOutline(state.tree).join("\n"); }
function fillJsonPane() { jsonInput.value = JSON.stringify(exportDoc(exportCtx()), null, 2); }
function fillTasksPane() {
  const lines = leafLines(state.tree);
  const width = String(lines.length).length;
  tasksOutput.value = lines.map((line, i) => String(i + 1).padStart(width) + ". " + line).join("\n");
}
function fillReportPane() { reportOutput.value = buildReport(state.tree, { projectName: state.projectName, versions: state.versions }); }
function htmlCtx(): HtmlExportCtx {
  return { projectName: state.projectName, versions: state.versions, jiraUrl: state.jiraUrl };
}
// The HTML export has no textarea to fill — nobody reads the markup, and a few hundred kB of it
// in the DOM only slowed the tab down. Each button builds a fresh document at click time instead.
function htmlDoc() { return buildHtmlExport(state.tree, htmlCtx()); }
function fillJiraPane() {
  jiraOutput.value = toJiraCsv(state.tree, state.jiraDelim, jiraNewOnly.checked, jiraVersionedOnly.checked);
}
jiraNewOnly.onchange = fillJiraPane;       // re-export when either export filter toggles
jiraVersionedOnly.onchange = fillJiraPane;
function fillComparePane() { compareResult.innerHTML = ""; } // start each open with a clean result area

let jiraSubTab = "export"; // remembered across modal opens
function showJiraSubTab(name: string): void {
  jiraSubTab = name;
  const pane = ioModal.querySelector('[data-pane="jira"]') as HTMLElement;
  pane.querySelectorAll(".subtab").forEach((b) => b.classList.toggle("active", (b as HTMLElement).dataset.jtab === name));
  pane.querySelectorAll(".io-subpane").forEach((p) => { (p as HTMLElement).hidden = (p as HTMLElement).dataset.jtab !== name; });
  if (name === "export") fillJiraPane();       // the export is regenerated from the plan each time
  if (name === "compare") fillComparePane();   // clear a stale result
  const focusMap: Record<string, HTMLElement> = {
    export: jiraOutput, keys: jiraKeysInput, import: jiraImportInput, compare: compareInput,
  };
  focusMap[name]?.focus();
}
ioModal.querySelectorAll(".subtab").forEach((b) => {
  (b as HTMLElement).onclick = () => showJiraSubTab((b as HTMLElement).dataset.jtab!);
});

function showIoTab(tab: string) {
  const panes: Record<string, [HTMLElement, () => void]> = {
    report: [reportOutput, fillReportPane],
    html: [byId("htmlDownload"), () => {}], // no field to fill; focus lands on the primary action
    text: [textInput, fillTextPane],
    json: [jsonInput, fillJsonPane],
    tasks: [tasksOutput, fillTasksPane],
  };
  ioModal.querySelectorAll(".tab").forEach((t) => t.classList.toggle("active", (t as HTMLElement).dataset.tab === tab));
  ioModal.querySelectorAll(".io-pane").forEach((p) => { (p as HTMLElement).hidden = (p as HTMLElement).dataset.pane !== tab; });
  // Jira is a group: its sub-tab decides what to fill and what to focus, so don't then focus and
  // select this tab's "field" over the top of it (that would select the hidden export textarea).
  if (tab === "jira") { showJiraSubTab(jiraSubTab); return; }
  const [field, fill] = panes[tab] || panes.report;
  fill();
  const el = field as HTMLElement & { select?: () => void };
  el.focus();
  el.select?.(); // a button target (the HTML tab) has nothing to select
}
function openIoModal(tab = "report") { ioModal.hidden = false; showIoTab(tab); }
function closeIoModal() { ioModal.hidden = true; }

byId("ioBtn").onclick = () => openIoModal("report");
byId("ioClose").onclick = closeIoModal;
ioModal.addEventListener("click", (e) => { if (e.target === ioModal) closeIoModal(); });
ioModal.querySelectorAll(".tab").forEach((t) => { (t as HTMLElement).onclick = () => showIoTab((t as HTMLElement).dataset.tab!); });
byId("reportCopy").onclick = (e) => copyToClipboard(reportOutput.value, e.currentTarget as HTMLElement, "✓ Copied");
byId("textCopy").onclick = (e) => copyToClipboard(textInput.value, e.currentTarget as HTMLElement, "✓ Copied");
byId("tasksCopy").onclick = (e) => copyToClipboard(tasksOutput.value, e.currentTarget as HTMLElement, "✓ Copied");
byId("jsonCopy").onclick = (e) => copyToClipboard(jsonInput.value, e.currentTarget as HTMLElement, "✓ Copied");

/* ---------- Read-only HTML export ---------- */
byId("htmlDownload").onclick = () => {
  downloadText(htmlDoc(), htmlExportFilename(htmlCtx()), "text/html");
};

// Preview in a new tab. The blob URL is this app's origin but is never written into the export
// itself — it's a throwaway handle for the preview tab, revoked once it has loaded.
byId("htmlPreview").onclick = () => {
  const url = URL.createObjectURL(new Blob([htmlDoc()], { type: "text/html" }));
  const win = window.open(url, "_blank");
  if (!win) alert("Your browser blocked the preview tab — use Download instead.");
  setTimeout(() => URL.revokeObjectURL(url), 60000);
};

byId("textDo").onclick = () => {
  const parsed = parseOutline(textInput.value);
  if (parsed.length === 0) { alert("Nothing to import — paste an outline first."); return; }
  if (textAppend.checked) state.tree = state.tree.concat(parsed);
  else state.tree = parsed;
  textAppend.checked = false;
  closeIoModal();
  commit();
};

// save `text` to the user's downloads as `filename` (shared by the JSON / HTML / Jira exports)
function downloadText(text: string, filename: string, mime: string): void {
  const url = URL.createObjectURL(new Blob([text], { type: mime }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

byId("jsonDownload").onclick = () => {
  downloadText(JSON.stringify(exportDoc(exportCtx()), null, 2), exportFilename(exportCtx()), "application/json");
};

byId("jsonUpload").onclick = () => {
  const picker = document.createElement("input");
  picker.type = "file";
  picker.accept = "application/json,.json";
  picker.onchange = () => {
    const file = picker.files && picker.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => { jsonInput.value = String(reader.result); jsonInput.focus(); };
    reader.onerror = () => alert("Could not read that file.");
    reader.readAsText(file);
  };
  picker.click();
};

byId("jsonDo").onclick = async () => {
  let parsed: any;
  try { parsed = JSON.parse(jsonInput.value); }
  catch (err: any) { alert("Invalid JSON: " + err.message); return; }
  const incoming = Array.isArray(parsed) ? parsed : parsed && parsed.tree;
  if (!Array.isArray(incoming)) { alert('JSON must be a task array, or an object with a "tree" array.'); return; }
  if (jsonAppend.checked) state.tree = state.tree.concat(normalize(incoming));
  else if (Array.isArray(parsed)) state.tree = normalize(parsed);
  else applyData(parsed); // restores tree + metadata, ignoring fields an older export omits
  jsonAppend.checked = false;
  closeIoModal();
  projTitle.value = state.projectName;
  document.title = state.projectName.trim() || "Untitled project";
  syncViewDialog();
  if (state.mode === "server") await relinkServerVersion();
  commit();
};

/* ---------- Jira CSV ---------- */
byId("jiraCopy").onclick = (e) => copyToClipboard(jiraOutput.value, e.currentTarget as HTMLElement, "✓ Copied");

byId("jiraDownload").onclick = () => {
  downloadText(jiraOutput.value, slug(state.projectName) + "-jira.csv", "text/csv");
};

/* ---------- Jira keys tab: link tickets to existing tasks, keys only ---------- */
const jiraKeysInput = asArea("jiraKeysInput");
byId("jiraKeysUpload").onclick = () => {
  const picker = document.createElement("input");
  picker.type = "file";
  picker.accept = "text/csv,.csv";
  picker.onchange = () => {
    const file = picker.files && picker.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => { jiraKeysInput.value = String(reader.result); jiraKeysInput.focus(); };
    reader.onerror = () => showJiraResult("Jira keys", '<p class="jr-summary">Could not read that file.</p>');
    reader.readAsText(file);
  };
  picker.click();
};
// straight to the step-2 diff: "keys" mode has nothing to configure, so there is no step 1
byId("jiraKeysImport").onclick = () => {
  const rows = parseJiraCsv(jiraKeysInput.value);
  if (!rows.length) {
    showJiraResult("Jira keys", '<p class="jr-summary">No CSV rows found — paste or upload a Jira CSV first.</p>');
    return;
  }
  reviewPlan = planJiraImport(state.tree, rows, "keys", state.jiraDelim);
  renderJiraDiff(reviewPlan);
  jiraDiffModal.hidden = false;
};


/* ---------- Jira import wizard: 1) mode → 2) review diff → 3) approve → 4) result ---------- */
const jiraImportModal = byId("jiraImportModal");
const jiraDiffModal = byId("jiraDiffModal");
const jiraResultModal = byId("jiraResultModal");
const jiraDiffBody = byId("jiraDiffBody");
function closeJiraImportModal() { jiraImportModal.hidden = true; }
function closeJiraDiffModal() { jiraDiffModal.hidden = true; }
function closeJiraResultModal() { jiraResultModal.hidden = true; }

function esc(s: string): string {
  return s.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c] as string));
}

// A Jira key chip for the innerHTML-built panes: a link when a ticket URL template is
// configured, a plain chip otherwise (never invent a link). `.jr-key` already styles both, and
// a.jr-key:hover / :visited exist, so linking these needs no new CSS. The URL is escaped too —
// it comes from a user-supplied template.
function jiraKeyHtml(key: string, cls = "jr-key"): string {
  if (!key) return `<span class="${cls}">—</span>`;
  const url = jiraTicketUrl(key);
  return url
    ? `<a class="${cls}" href="${esc(url)}" target="_blank" rel="noopener noreferrer" title="Open Jira issue ${esc(key)} in a new tab">${esc(key)}</a>`
    : `<span class="${cls}">${esc(key)}</span>`;
}

const FIELD_LABEL: Record<string, string> = {
  jiraKey: "Jira key", name: "Name", done: "Status", version: "Version",
  component: "Component", estimate: "Estimate", move: "Moved",
};
// format a raw field value for display (days for estimate, done/not-done for status)
function fieldValueText(field: JiraField, v: string | number | boolean): string {
  if (field === "done") return v ? "done" : "not done";
  if (field === "estimate") return v === "" || v == null ? "—" : fmt(Number(v)) + "d";
  return v ? String(v) : "—";
}
function showJiraResult(title: string, bodyHtml: string) {
  byId("jiraResultTitle").textContent = title;
  byId("jiraResultBody").innerHTML = bodyHtml;
  jiraResultModal.hidden = false;
}

// the plan currently under review (step 2/3); checkboxes toggle its `selected` flags
let reviewPlan: JiraPlan | null = null;

// STEP 2 — render the reviewable diff (matched field changes + creations + unmatched)
// Every selectable thing in the review is an object with a `selected` flag (a field change, a
// move, or a task to create). A toggle just walks the ones it owns and re-renders, so the master
// and the per-section toggles are the same three lines and can't disagree with the checkboxes.
type Selectable = { selected: boolean };
function sectionSelectables(plan: JiraPlan, which: "changes" | "creates" | "all"): Selectable[] {
  const fromMatches = plan.matches.flatMap((m) => [...m.changes, ...(m.move ? [m.move] : [])]) as Selectable[];
  if (which === "changes") return fromMatches;
  if (which === "creates") return plan.creates as Selectable[];
  return [...fromMatches, ...(plan.creates as Selectable[])];
}
// a "select all" box that shows mixed state when only some are ticked
function selectAllBox(items: Selectable[], label: string, onToggle: () => void): HTMLElement {
  const wrap = document.createElement("label");
  wrap.className = "jd-selectall";
  const cb = document.createElement("input");
  cb.type = "checkbox";
  const on = items.filter((i) => i.selected).length;
  cb.checked = on === items.length && items.length > 0;
  cb.indeterminate = on > 0 && on < items.length;
  cb.onchange = () => { items.forEach((i) => (i.selected = cb.checked)); onToggle(); };
  const txt = document.createElement("span");
  txt.textContent = `${label} (${on}/${items.length})`;
  wrap.appendChild(cb); wrap.appendChild(txt);
  return wrap;
}

function renderJiraDiff(plan: JiraPlan): void {
  // keys mode has no step 1, so the button cancels rather than going back
  byId("jiraDiffBack").textContent = plan.mode === "keys" ? "Cancel" : "← Back";
  jiraDiffBody.innerHTML = "";
  const changeCount = plan.matches.reduce((s, m) => s + m.changes.length + (m.move ? 1 : 0), 0);

  const summary = document.createElement("p");
  summary.className = "jr-summary";
  summary.innerHTML =
    `<b>${plan.mode === "attach" ? "Merge & attach" : "Merge"}</b> — ${plan.matchedCount} matched, ` +
    `${plan.matches.length} with changes (${changeCount} field${changeCount === 1 ? "" : "s"})` +
    (plan.mode === "attach" ? `, ${plan.creates.length} to create` : "") +
    `, ${plan.unmatched.length} unmatched` +
    (plan.ignoredType.length ? `, ${plan.ignoredType.length} ignored (Epics)` : "") +
    `. Untick anything you don't want to apply.`;
  jiraDiffBody.appendChild(summary);
  const everything = sectionSelectables(plan, "all");
  if (everything.length)
    jiraDiffBody.appendChild(selectAllBox(everything, "Select everything", () => renderJiraDiff(plan)));

  if (plan.matches.length) {
    const h = document.createElement("h3");
    // "keys" only ever writes one field, so say which; other modes span several
    h.textContent = (plan.mode === "keys" ? "Update Jira key" : "Changes to matched tasks") + ` — ${plan.matches.length}`;
    jiraDiffBody.appendChild(h);
    const own = sectionSelectables(plan, "changes");
    if (own.length > 1) jiraDiffBody.appendChild(selectAllBox(own, "All of these", () => renderJiraDiff(plan)));
    plan.matches.forEach((m) => {
      const box = document.createElement("div"); box.className = "jd-task";
      const head = document.createElement("div"); head.className = "jd-path";
      head.innerHTML = (m.key ? jiraKeyHtml(m.key) : "") + `<span class="jr-path">${esc(m.path)}</span>`;
      box.appendChild(head);
      m.changes.forEach((c) => {
        const label = document.createElement("label"); label.className = "jd-change";
        const cb = document.createElement("input"); cb.type = "checkbox"; cb.checked = c.selected;
        cb.onchange = () => { c.selected = cb.checked; };
        const txt = document.createElement("span");
        txt.innerHTML = `<b>${FIELD_LABEL[c.field]}</b>: <span class="jd-from">${esc(fieldValueText(c.field, c.from))}</span>` +
          ` → <span class="jd-to">${esc(fieldValueText(c.field, c.to))}</span>`;
        label.appendChild(cb); label.appendChild(txt); box.appendChild(label);
      });
      if (m.move) {
        const mv = m.move;
        const label = document.createElement("label"); label.className = "jd-change";
        const cb = document.createElement("input"); cb.type = "checkbox"; cb.checked = mv.selected;
        cb.onchange = () => { mv.selected = cb.checked; };
        const txt = document.createElement("span");
        txt.innerHTML = `<b>Move</b>: <span class="jd-from">${esc(mv.fromPath)}</span> → <span class="jd-to">${esc(mv.toPath)}</span>`;
        label.appendChild(cb); label.appendChild(txt); box.appendChild(label);
      }
      jiraDiffBody.appendChild(box);
    });
  }

  if (plan.mode === "attach" && plan.creates.length) {
    const h = document.createElement("h3");
    h.textContent = `New tasks to create — ${plan.creates.length}`;
    jiraDiffBody.appendChild(h);
    if (plan.creates.length > 1)
      jiraDiffBody.appendChild(selectAllBox(plan.creates as Selectable[], "All of these", () => renderJiraDiff(plan)));
    plan.creates.forEach((cr) => {
      const label = document.createElement("label"); label.className = "jd-change";
      const cb = document.createElement("input"); cb.type = "checkbox"; cb.checked = cr.selected;
      cb.onchange = () => { cr.selected = cb.checked; };
      const meta = [cr.key, cr.reporter, cr.version, cr.component, cr.estimate != null ? fmt(cr.estimate) + "d" : ""]
        .filter(Boolean).join(" · ");
      const txt = document.createElement("span");
      txt.innerHTML = `<span class="jr-path">${esc(cr.path)}</span>` + (meta ? ` <span class="jd-meta">${esc(meta)}</span>` : "");
      label.appendChild(cb); label.appendChild(txt); jiraDiffBody.appendChild(label);
    });
  }

  // Two informational buckets so every CSV row is accounted for, not just the ones that change.
  // Collapsed: together they dwarf the actionable list (84 vs 12 on a real export), and nothing
  // in them is actionable — the count in the summary is what you normally want.
  const bucket = (title: string, items: { html: string }[]) => {
    if (!items.length) return;
    const d = document.createElement("details"); d.className = "jd-bucket";
    const sum = document.createElement("summary"); sum.textContent = `${title} — ${items.length}`;
    d.appendChild(sum);
    const ul = document.createElement("ul");
    items.forEach((it) => { const li = document.createElement("li"); li.innerHTML = it.html; ul.appendChild(li); });
    d.appendChild(ul);
    jiraDiffBody.appendChild(d);
  };

  bucket(plan.mode === "keys" ? "Already linked — nothing to change" : "Matched — nothing to change",
    plan.inSync.map((m) => ({
      html: jiraKeyHtml(m.key) + `<span class="jr-path">${esc(m.path)}</span>` +
        (m.note ? ` <span class="jd-meta">${esc(m.note)}</span>` : ""),
    })));

  bucket("Ignored", [
    ...plan.ignoredType.map((u) => ({ html: `<span class="jr-path">${esc(u)}</span> <span class="jd-meta">Epic — not a task</span>` })),
    ...plan.unmatched.map((u) => ({ html: `<span class="jr-path">${esc(u)}</span> <span class="jd-meta">no matching task</span>` })),
  ]);

  if (!plan.matches.length && !(plan.mode === "attach" && plan.creates.length)) {
    const p = document.createElement("p"); p.className = "jr-empty";
    p.textContent = "Nothing to merge — matched tasks already agree with the CSV.";
    jiraDiffBody.appendChild(p);
  }
}

jiraResultModal.addEventListener("click", (e) => { if (e.target === jiraResultModal) closeJiraResultModal(); });
byId("jiraResultClose").onclick = closeJiraResultModal;
jiraImportModal.addEventListener("click", (e) => { if (e.target === jiraImportModal) closeJiraImportModal(); });
byId("jiraImportCancel").onclick = closeJiraImportModal;
jiraDiffModal.addEventListener("click", (e) => { if (e.target === jiraDiffModal) closeJiraDiffModal(); });
byId("jiraDiffBack").onclick = () => {
  closeJiraDiffModal();
  // back to the mode chooser — but "keys" came straight from its own tab, so there is nothing
  // behind it; reopening the chooser would offer modes this flow never used and then run the
  // *other* tab's textarea through them.
  if (!reviewPlan || reviewPlan.mode !== "keys") jiraImportModal.hidden = false;
};

// STEP 1 — open the mode chooser
const jiraImportInput = asArea("jiraImportInput");
byId("jiraImportUpload").onclick = () => {
  const picker = document.createElement("input");
  picker.type = "file";
  picker.accept = "text/csv,.csv";
  picker.onchange = () => {
    const file = picker.files && picker.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => { jiraImportInput.value = String(reader.result); jiraImportInput.focus(); };
    reader.onerror = () => showJiraResult("Jira import", '<p class="jr-summary">Could not read that file.</p>');
    reader.readAsText(file);
  };
  picker.click();
};
byId("jiraImport").onclick = () => {
  if (!parseJiraCsv(jiraImportInput.value).length) {
    showJiraResult("Jira import", '<p class="jr-summary">No CSV rows found — paste or upload a Jira CSV first.</p>');
    return;
  }
  jiraImportModal.hidden = false;
};

// STEP 1 → 2 — compute the diff (no mutation) and open the review dialog
byId("jiraImportGo").onclick = () => {
  const rows = parseJiraCsv(jiraImportInput.value);
  const picked = jiraImportModal.querySelector('input[name="jiraMode"]:checked') as HTMLInputElement | null;
  const mode = picked && picked.value === "attach" ? "attach" : "map";
  reviewPlan = planJiraImport(state.tree, rows, mode, state.jiraDelim);
  closeJiraImportModal();
  renderJiraDiff(reviewPlan);
  jiraDiffModal.hidden = false;
};

// STEP 3 → 4 — approve: apply only the selected changes, then show the result
byId("jiraDiffApply").onclick = async () => {
  if (!reviewPlan) return;
  const { changed, created, unmatched } = applyJiraPlan(state.tree, reviewPlan);
  reviewPlan = null;
  closeJiraDiffModal();
  closeIoModal();
  syncViewDialog();
  if (state.mode === "server") await relinkServerVersion();
  commit();
  const summary = `<p class="jr-summary">Merged — updated ${changed.length} task${changed.length === 1 ? "" : "s"}` +
    (created.length ? `, created ${created.length}` : "") +
    (unmatched.length ? `, ${unmatched.length} unmatched` : "") + ".</p>";
  const changedSec = changed.length
    ? "<ul>" + changed.map((c) =>
        `<li>${jiraKeyHtml(c.key)}<span class="jr-path">${esc(c.path)}</span>` +
        `<span class="jd-meta">${c.fields.map((f) => FIELD_LABEL[f]).join(", ")}</span></li>`).join("") + "</ul>"
    : '<p class="jr-empty">No task changes applied.</p>';
  const createdSec = created.length
    ? "<ul>" + created.map((c) =>
        `<li>${jiraKeyHtml(c.key)}<span class="jr-path">${esc(c.path)}</span></li>`).join("") + "</ul>"
    : "";
  const body = `<h3>Updated — key · task · fields</h3>${changedSec}` + (created.length ? `<h3>Created</h3>${createdSec}` : "");
  showJiraResult("Merge complete", summary + body);
};

/* ---------- Compare tab: MPP-as-truth vs a Jira CSV export (read-only) ---------- */
byId("compareUpload").onclick = () => {
  const picker = document.createElement("input");
  picker.type = "file";
  picker.accept = "text/csv,.csv";
  picker.onchange = () => {
    const file = picker.files && picker.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => { compareInput.value = String(reader.result); compareInput.focus(); };
    reader.onerror = () => { compareResult.innerHTML = '<p class="jr-summary">Could not read that file.</p>'; };
    reader.readAsText(file);
  };
  picker.click();
};

// one plain-English instruction per drifted field (the MPP target value in bold/green)
// Compare is a guide for editing Jira by hand, so the new summary — the one value you actually
// retype over there — gets a copy button, placed against the value itself rather than at the end
// of the line so it's unambiguous *what* it copies. No leading space, for the same reason.
function cpy(value: string, what: string): string {
  return `<button class="copy-btn cmp-copy" data-copy="${esc(value)}" title="Copy the ${esc(what)}">⧉</button>`;
}
function compareActionHtml(c: CompareChange): string {
  const to = `<b class="jd-to">${esc(String(c.plan))}</b>`;
  switch (c.field) {
    case "name":
      // Compare's whole job is "go and fix this in Jira", so hand over the new name in one
      // click. The button is text-only because copyToClipboard swaps textContent while flashing;
      // the click is handled by one delegated listener on #compareResult (see renderCompare).
      return `Rename to ${to}${cpy(String(c.plan), "new summary")}` +
        ` <span class="jd-from">(Jira: ${esc(String(c.jira))})</span>`;
    case "status":
      return c.plan
        ? `Set status to <b class="jd-to">Done</b> <span class="jd-from">(currently ${esc(String(c.jira))})</span>`
        : `Reopen — set a non-done status <span class="jd-from">(currently ${esc(String(c.jira))})</span>`;
    case "version":
      return `Add Fix version ${to} <span class="jd-from">(Jira: ${esc(String(c.jira) || "none")})</span>`;
    case "component":
      return c.plan
        ? `Assign component ${to} <span class="jd-from">(Jira: ${esc(String(c.jira) || "—")})</span>`
        : `Clear component <span class="jd-from">(Jira: ${esc(String(c.jira))})</span>`;
    case "estimate":
      return `Adjust estimate to <b class="jd-to">${esc(fmt(Number(c.plan)))}d</b>` +
        ` <span class="jd-from">(Jira: ${esc(fmt(Number(c.jira)))}d)</span>`;
  }
}

// the Jira key whose link was opened most recently — marked so you can find your place
// after tabbing back. Persists across re-compares / scope changes (re-applied on render).
let lastOpenedKey: string | null = null;
function markOpened(key: string): void {
  lastOpenedKey = key;
  compareResult.querySelectorAll<HTMLElement>(".jd-task").forEach((box) => {
    const active = box.dataset.key === key;
    box.classList.toggle("jd-active", active);
    const existing = box.querySelector(".jd-active-tag");
    if (active && !existing) {
      const tag = document.createElement("span");
      tag.className = "jd-active-tag";
      tag.textContent = "● last opened";
      box.querySelector(".jd-path")?.appendChild(tag);
    } else if (!active && existing) existing.remove();
  });
}

function renderCompare(cmp: JiraCompare, scope: CompareScope): void {
  compareResult.innerHTML = "";
  const head = document.createElement("div");
  head.className = "jr-head";
  const summary = document.createElement("p");
  summary.className = "jr-summary";
  summary.innerHTML = cmp.update.length
    ? `<b>${cmp.update.length}</b> ticket${cmp.update.length === 1 ? "" : "s"} to update in Jira ` +
      `<span class="jd-meta">(${cmp.matchedCount} matched)</span>. Your plan is the source of truth — ` +
      `open each ticket and apply the changes below.`
    : `Checked ${cmp.matchedCount} matched ticket${cmp.matchedCount === 1 ? "" : "s"}.`;
  if (cmp.ignoredType.length) // Epics — say so rather than quietly dropping them
    summary.innerHTML += ` <span class="jd-meta">${cmp.ignoredType.length} ignored (Epics)</span>`;
  head.appendChild(summary);
  // shareable plain-text version of exactly this result — for pasting to the team
  const copyBtn = document.createElement("button");
  copyBtn.className = "tool jr-copy";
  copyBtn.textContent = "Copy report";
  copyBtn.title = "Copy a shareable plain-text version of this report";
  copyBtn.onclick = (e) => copyToClipboard(compareReport(cmp, { projectName: state.projectName, scope }), e.currentTarget as HTMLElement, "✓ Copied");
  head.appendChild(copyBtn);
  compareResult.appendChild(head);

  cmp.update.forEach((t) => {
    const box = document.createElement("div"); box.className = "jd-task";
    if (t.key) box.dataset.key = t.key; // so markOpened() can find this box on link click
    const head = document.createElement("div"); head.className = "jd-path";
    if (t.key) {
      const url = jiraTicketUrl(t.key);
      const kb = document.createElement(url ? "a" : "span");
      kb.className = "jr-key";
      kb.textContent = t.key;
      if (url) {
        (kb as HTMLAnchorElement).href = url;
        (kb as HTMLAnchorElement).target = "_blank";
        (kb as HTMLAnchorElement).rel = "noopener noreferrer";
        kb.title = "Open Jira issue " + t.key + " in a new tab";
        kb.onclick = () => markOpened(t.key); // remember which ticket we jumped to (opens a new tab)
      }
      head.appendChild(kb);
    }
    const path = document.createElement("span"); path.className = "jr-path"; path.textContent = t.path;
    head.appendChild(path);
    box.appendChild(head);
    t.changes.forEach((c) => {
      const line = document.createElement("div"); line.className = "jd-change";
      line.innerHTML = compareActionHtml(c);
      box.appendChild(line);
    });
    compareResult.appendChild(box);
  });
  if (lastOpenedKey) markOpened(lastOpenedKey); // re-highlight after a re-render, if still shown

  if (!cmp.update.length) {
    const p = document.createElement("p"); p.className = "jr-empty";
    p.textContent = cmp.matchedCount
      ? "Every matched ticket already matches the plan."
      : "No tickets matched — check the CSV has Issue key / Summary columns.";
    compareResult.appendChild(p);
  }

  // the other half of the answer: tickets that exist in Jira but nowhere in the plan
  if (cmp.unmatched.length) {
    const h = document.createElement("h3");
    h.textContent = `Not in the plan — ${cmp.unmatched.length} ticket${cmp.unmatched.length === 1 ? "" : "s"}`;
    compareResult.appendChild(h);
    const ul = document.createElement("ul");
    cmp.unmatched.forEach((u) => { const li = document.createElement("li"); li.textContent = u; ul.appendChild(li); });
    compareResult.appendChild(ul);
  }
}

// One delegated listener for the per-rename copy buttons: compareActionHtml emits them as HTML,
// so they can't carry their own handler. `data-copy` holds the plan's name — the value Jira
// should end up with.
compareResult.addEventListener("click", (e) => {
  const btn = (e.target as HTMLElement).closest(".cmp-copy") as HTMLElement | null;
  if (!btn) return;
  copyToClipboard(btn.dataset.copy || "", btn, "✓");
});

const compareScopeSel = byId("compareScope") as HTMLSelectElement;
function runCompare() {
  const rows = parseJiraCsv(compareInput.value);
  if (!rows.length) {
    compareResult.innerHTML = '<p class="jr-summary">No CSV rows found — paste or upload a Jira CSV export first.</p>';
    return;
  }
  const scope = compareScopeSel.value as CompareScope;
  renderCompare(compareJira(state.tree, rows, state.jiraDelim, scope), scope);
}
byId("compareGo").onclick = runCompare;
// changing the scope re-runs against the CSV already entered (no need to click Compare again)
compareScopeSel.onchange = () => { if (compareInput.value.trim()) runCompare(); };

/* ---------- Project modal (tabs: Versions · Components · Settings) ---------- */
const versionsInput = asArea("versionsInput");
const componentsInput = asArea("componentsInput");
const estimationsInput = asArea("estimationsInput");
const jiraUrlInput = asInput("jiraUrlInput");
const jiraDelimInput = asInput("jiraDelimInput");
const countDepsInput = asInput("countDepsInput");
const depDocOrderInput = asInput("depDocOrderInput");
const defaultEstInput = asInput("defaultEstInput");
const complexityInput = asInput("complexityInput");

function showProjectTab(tab: string) {
  projectModal.querySelectorAll(".tab").forEach((t) => t.classList.toggle("active", (t as HTMLElement).dataset.ptab === tab));
  projectModal.querySelectorAll(".io-pane").forEach((p) => { (p as HTMLElement).hidden = (p as HTMLElement).dataset.ppane !== tab; });
  const field = tab === "components" ? componentsInput : tab === "estimations" ? estimationsInput
    : tab === "settings" ? jiraUrlInput : versionsInput;
  field.focus();
}
function openProjectModal(tab = "versions") {
  versionsInput.value = state.versions.join("\n");
  componentsInput.value = state.components.join("\n");
  estimationsInput.value = state.estimations.join("\n");
  jiraUrlInput.value = state.jiraUrl;
  jiraDelimInput.value = state.jiraDelim;
  countDepsInput.checked = state.countDeps;
  depDocOrderInput.checked = state.depDocOrder;
  complexityInput.checked = state.showComplexity;
  defaultEstInput.value = String(state.defaultEstimate);
  projectModal.hidden = false;
  showProjectTab(tab);
}
function closeProjectModal() { projectModal.hidden = true; }

byId("projectBtn").onclick = () => openProjectModal("versions");
byId("projectClose").onclick = closeProjectModal;
projectModal.addEventListener("click", (e) => { if (e.target === projectModal) closeProjectModal(); });
projectModal.querySelectorAll(".tab").forEach((t) => { (t as HTMLElement).onclick = () => showProjectTab((t as HTMLElement).dataset.ptab!); });

byId("versionsDo").onclick = () => {
  // supports plain versions and "old -> new" rename lines (relinks assigned tasks)
  state.versions = applyVersionEdits(state.tree, parseVersionLines(versionsInput.value));
  closeProjectModal();
  commit();
};
byId("componentsDo").onclick = () => {
  state.components = applyVersionEdits(state.tree, parseVersionLines(componentsInput.value), "component");
  closeProjectModal();
  commit();
};
byId("estimationsDo").onclick = () => {
  // parse the names (supports "old -> new" renames of a stream); migrate per-task values,
  // then re-derive each leaf's total from the new stream set.
  state.estimations = applyEstimationEdits(state.tree, parseVersionLines(estimationsInput.value));
  resyncEstimateTotals(state.tree, state.estimations);
  closeProjectModal();
  commit();
};
byId("settingsDo").onclick = () => {
  state.jiraUrl = jiraUrlInput.value.trim(); // "{key}" template for clickable ticket links
  state.jiraDelim = jiraDelimInput.value.trim() || ">"; // Summary level delimiter; never empty
  state.countDeps = countDepsInput.checked; // show tagged-dependency "extra" work in the Est column
  state.depDocOrder = depDocOrderInput.checked; // Gantt dependency order: document vs feature-grouped
  state.showComplexity = complexityInput.checked; // per-task complexity dots on/off
  const de = parseFloat(defaultEstInput.value); // estimate new tasks start at (0 when laying out structure)
  state.defaultEstimate = Number.isFinite(de) && de >= 0 ? de : 1;
  closeProjectModal();
  commit();
};

/* ---------- global Escape ---------- */
/* ---------- recent-plans menu (top-left hamburger, localStorage) ---------- */
const recentsBtn = byId("recentsBtn");
const recentsMenu = byId("recentsMenu");
function renderRecentsMenu(): void {
  recentsMenu.innerHTML = "";

  // first item: back to the landing page listing every plan as a widget
  const home = document.createElement("a");
  home.className = "recents-home";
  home.href = "/";
  home.innerHTML = '<span class="recents-home-icon">▦</span>Dashboard';
  home.title = "All plans";
  recentsMenu.appendChild(home);
  const sep = document.createElement("div");
  sep.className = "recents-sep";
  recentsMenu.appendChild(sep);

  const title = document.createElement("div");
  title.className = "recents-title";
  title.textContent = "Recent plans";
  recentsMenu.appendChild(title);
  const plans = listRecents().slice().sort((a, b) =>
    (a.name || "").localeCompare(b.name || "", undefined, { numeric: true, sensitivity: "base" }));
  if (!plans.length) {
    const empty = document.createElement("div");
    empty.className = "recents-empty";
    empty.textContent = "No recent plans yet. Open a plan by its URL and it'll appear here.";
    recentsMenu.appendChild(empty);
    return;
  }
  for (const p of plans) {
    const row = document.createElement("div");
    row.className = "recents-item" + (p.guid === state.guid ? " current" : "");
    const name = document.createElement("button");
    name.className = "recents-name";
    name.textContent = p.name || "Untitled plan";
    name.title = "Open " + (p.name || p.guid);
    name.onclick = () => { location.href = "/plan/" + p.guid; }; // full reload into that plan
    const x = document.createElement("button");
    x.className = "recents-x";
    x.textContent = "×";
    x.title = "Remove from this list";
    x.onclick = (e) => {
      e.stopPropagation();
      removeRecent(p.guid);
      clearCollapse(p.guid); clearPrefs(p.guid); // also drop this plan's local view state
      renderRecentsMenu();
    };
    row.append(name, x);
    recentsMenu.appendChild(row);
  }
}
function openRecents(): void { closeHeaderMenus(); renderRecentsMenu(); recentsMenu.hidden = false; }
function closeRecents(): void { recentsMenu.hidden = true; }
recentsBtn.onclick = (e) => { e.stopPropagation(); recentsMenu.hidden ? openRecents() : closeRecents(); };
document.addEventListener("mousedown", (e) => { // outside-click closes the menu
  if (!recentsMenu.hidden && !recentsMenu.contains(e.target as Node) && e.target !== recentsBtn) closeRecents();
});

/* ---------- header dropdown menus (View ▾ / ⋯ More) — same popover UX as recents ---------- */
const headerMenus: { menu: HTMLElement; btn: HTMLElement }[] = [];
function closeHeaderMenus(): void { headerMenus.forEach((m) => (m.menu.hidden = true)); }
function wireMenu(btnId: string, menuId: string, onOpen?: () => void): void {
  const btn = byId(btnId), menu = byId(menuId);
  headerMenus.push({ menu, btn });
  btn.onclick = (e) => {
    e.stopPropagation();
    const willOpen = menu.hidden;
    closeHeaderMenus(); closeRecents(); // only one menu open at a time
    if (willOpen) { onOpen?.(); menu.hidden = false; }
  };
  // an action item closes the menu; the show-column checkboxes stay open for multi-toggling
  menu.addEventListener("click", (e) => { if ((e.target as HTMLElement).closest(".menu-item")) menu.hidden = true; });
}
wireMenu("viewMenuBtn", "viewMenu", syncViewDialog); // refresh the checkboxes to current state on open
wireMenu("moreBtn", "moreMenu");
document.addEventListener("mousedown", (e) => { // outside-click closes any open header menu
  const t = e.target as Node;
  headerMenus.forEach(({ menu, btn }) => { if (!menu.hidden && !menu.contains(t) && !btn.contains(t)) menu.hidden = true; });
});

// keyboard-shortcuts help — floating "?" button + the "?" key both open it
const helpModal = byId("helpModal");
function openHelp() { helpModal.hidden = false; }
function closeHelp() { helpModal.hidden = true; }
byId("helpBtn").onclick = openHelp;
byId("helpClose").onclick = closeHelp;
helpModal.addEventListener("click", (e) => { if (e.target === helpModal) closeHelp(); });
// true while the user is typing somewhere (so "?" types a literal "?" instead of opening help)
function isTypingTarget(t: EventTarget | null): boolean {
  const el = t as HTMLElement | null;
  if (!el) return false;
  return el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.tagName === "SELECT" || el.isContentEditable;
}

document.addEventListener("keydown", (e) => {
  if (e.key === "?" && !e.metaKey && !e.ctrlKey && !e.altKey && !isTypingTarget(e.target)) {
    e.preventDefault(); openHelp(); return;
  }
  if (e.key !== "Escape") return;
  if (!helpModal.hidden) { closeHelp(); return; }
  if (!recentsMenu.hidden) { closeRecents(); return; }
  if (headerMenus.some((m) => !m.menu.hidden)) { closeHeaderMenus(); return; }
  if (commentIsOpen()) { closeCommentPop(); return; }
  if (!historyModal.hidden) { closeHistoryModal(); return; }
  if (!jiraResultModal.hidden) { closeJiraResultModal(); return; }
  if (!jiraDiffModal.hidden) { closeJiraDiffModal(); return; }
  if (!jiraImportModal.hidden) { closeJiraImportModal(); return; }
  if (!ioModal.hidden) { closeIoModal(); return; }
  if (!projectModal.hidden) { closeProjectModal(); return; }
  if (state.activeTag) toggleActiveTag(state.activeTag);
});
